import React, { useState, useEffect } from 'react';
import {
  Grid,
  Input,
  NativeSelect,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import Header from '../../../layout/Header';
import Subheader from '../../../layout/Subheader';
import Loading from '../../../components/Loading';
import { useMessageBox } from '../../../context/MessageBox';
import {
  consultarId,
  adicionar,
  atualizar,
  TIPOS_AUTENTICACAO,
} from '../../../services/erpsService';
import { formatDateTimeToRender } from '../../../utils/functions';
import useStyles from './style';

const EMPTY_FORM = {
  erp: '',
  versao: '',
  urlBase: '',
  endpointToken: '',
  endpointConsultaTitulos: '',
  endpointConsultaFornecedores: '',
  endpointConsultaLojas: '',
  endpointDuplicataNegociacaoAbrir: '',
  endpointDuplicataNegociacaoLiberar: '',
  endpointDuplicataAnteciparTitulo: '',
  endpointDuplicataTrocarCedente: '',
  tipoAutenticacao: 'OAuth2ClientCredentials',
  status: 'Ativo',
  dataRegistro: '',
  dataAtualizacao: '',
};

export function ERPRegister() {
  const classes = useStyles();
  const history = useHistory();
  const { id } = useParams();
  const { setMessageData } = useMessageBox();

  const [isLoading, setLoading] = useState(true);
  const [isRegister, setIsRegister] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [hasFormChanged, setHasFormChanged] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const handleInput = (event, value) => {
    event.persist();
    setForm((prev) => ({
      ...prev,
      [event.target.name]: value !== undefined ? value : event.target.value,
    }));
    setHasFormChanged(true);
  };

  const fetchOne = async (perfilId) => {
    try {
      const res = await consultarId(perfilId);
      if (res?.data?.complemento?.mensagensBox?.length > 0) {
        setMessageData(res.data.complemento.mensagensBox);
      }
      if (res.data.status !== 'Sucesso') {
        (res.data.resposta || []).forEach((el) => toast.error(el.message));
        return;
      }
      const r = res.data.resposta;
      setForm({
        erp: r.erp || '',
        versao: r.versao || '',
        urlBase: r.urlBase || '',
        endpointToken: r.endpointToken || '',
        endpointConsultaTitulos: r.endpointConsultaTitulos || '',
        endpointConsultaFornecedores: r.endpointConsultaFornecedores || '',
        endpointConsultaLojas: r.endpointConsultaLojas || '',
        endpointDuplicataNegociacaoAbrir: r.endpointDuplicataNegociacaoAbrir || '',
        endpointDuplicataNegociacaoLiberar: r.endpointDuplicataNegociacaoLiberar || '',
        endpointDuplicataAnteciparTitulo: r.endpointDuplicataAnteciparTitulo || '',
        endpointDuplicataTrocarCedente: r.endpointDuplicataTrocarCedente || '',
        tipoAutenticacao: r.tipoAutenticacao || 'OAuth2ClientCredentials',
        status: r.status || 'Ativo',
        dataRegistro: r.dataRegistro || '',
        dataAtualizacao: r.dataAtualizacao || '',
      });
    } catch (err) {
      toast.error('Falha ao carregar o perfil ERP.');
    }
  };

  const initialize = async () => {
    setLoading(true);
    if (window.location.pathname.includes('cadastrar')) {
      setIsRegister(true);
      setIsEditing(true);
    } else if (id) {
      setIsEditing(window.location.pathname.includes('editar'));
      await fetchOne(id);
    }
    setLoading(false);
  };

  useEffect(() => {
    initialize();
  }, [id]);

  const validar = () => {
    if (!form.erp?.trim()) return 'Informe o nome do ERP.';
    if (!form.versao?.trim()) return 'Informe a versão.';
    if (!form.urlBase?.trim()) return 'Informe a URL base.';
    if (!form.endpointToken?.trim()) return 'Informe o endpoint de token.';
    if (!form.endpointConsultaTitulos?.trim())
      return 'Informe o endpoint de consulta de títulos.';
    if (!form.tipoAutenticacao) return 'Selecione o tipo de autenticação.';
    if (!form.status) return 'Selecione o status.';
    return null;
  };

  const onSubmit = async () => {
    const erroValidacao = validar();
    if (erroValidacao) {
      toast.warn(erroValidacao);
      return;
    }
    setLoading(true);
    try {
      if (isRegister) {
        const payload = {
          erp: form.erp,
          versao: form.versao,
          urlBase: form.urlBase,
          endpointToken: form.endpointToken,
          endpointConsultaTitulos: form.endpointConsultaTitulos,
          endpointConsultaFornecedores: form.endpointConsultaFornecedores,
          endpointConsultaLojas: form.endpointConsultaLojas,
          endpointDuplicataNegociacaoAbrir: form.endpointDuplicataNegociacaoAbrir,
          endpointDuplicataNegociacaoLiberar: form.endpointDuplicataNegociacaoLiberar,
          endpointDuplicataAnteciparTitulo: form.endpointDuplicataAnteciparTitulo,
          endpointDuplicataTrocarCedente: form.endpointDuplicataTrocarCedente,
          tipoAutenticacao: form.tipoAutenticacao,
          status: form.status,
        };
        const res = await adicionar(payload);
        if (res?.data?.complemento?.mensagensBox?.length > 0) {
          setMessageData(res.data.complemento.mensagensBox);
        }
        if (res.data.status !== 'Sucesso') {
          (res.data.resposta || []).forEach((el) => toast.error(el.message));
          return;
        }
        toast.success('Perfil ERP cadastrado com sucesso!');
        history.push(
          `/app/erp/${res.data.resposta.importacaoTituloApiErpId}/editar`,
        );
      } else if (id) {
        const payload = {
          importacaoTituloApiErpId: Number(id),
          erp: form.erp,
          versao: form.versao,
          urlBase: form.urlBase,
          endpointToken: form.endpointToken,
          endpointConsultaTitulos: form.endpointConsultaTitulos,
          endpointConsultaFornecedores: form.endpointConsultaFornecedores,
          endpointConsultaLojas: form.endpointConsultaLojas,
          endpointDuplicataNegociacaoAbrir: form.endpointDuplicataNegociacaoAbrir,
          endpointDuplicataNegociacaoLiberar: form.endpointDuplicataNegociacaoLiberar,
          endpointDuplicataAnteciparTitulo: form.endpointDuplicataAnteciparTitulo,
          endpointDuplicataTrocarCedente: form.endpointDuplicataTrocarCedente,
          tipoAutenticacao: form.tipoAutenticacao,
          status: form.status,
          dataAtualizacao: form.dataAtualizacao,
        };
        const res = await atualizar(payload);
        if (res?.data?.complemento?.mensagensBox?.length > 0) {
          setMessageData(res.data.complemento.mensagensBox);
        }
        if (res.data.status !== 'Sucesso') {
          (res.data.resposta || []).forEach((el) => toast.error(el.message));
          return;
        }
        if (res.data.resposta?.dataAtualizacao) {
          setForm((prev) => ({
            ...prev,
            dataAtualizacao: res.data.resposta.dataAtualizacao,
          }));
        }
        toast.success('Perfil ERP alterado com sucesso!');
        setHasFormChanged(false);
      }
    } catch (err) {
      const msg = err?.response?.data?.resposta?.[0]?.message;
      toast.error(msg || 'Detectamos um problema interno! tente novamente em instantes.');
    } finally {
      setLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className={classes.root}>
        <Header />
        <Subheader back='/app/erps' title='Perfil ERP' />
        <Loading />
      </div>
    );
  }

  const subtitulo = isRegister
    ? 'Cadastrando Perfil ERP'
    : isEditing
    ? `Atualizando Perfil ${form.erp}`
    : `Visualizando Perfil ${form.erp}`;

  return (
    <div className={classes.root}>
      <Header />
      {isEditing ? (
        <Subheader
          title={subtitulo}
          back='/app/erps'
          isDisabled={isLoading || !hasFormChanged}
          loading={isLoading || !hasFormChanged}
          submit={onSubmit}
          submitText='Gravar'
        />
      ) : (
        <Subheader back='/app/erps' loading={isLoading} title={subtitulo} />
      )}
      <div className={classes.content}>
        <div className={classes.rootForm}>
          <Accordion defaultExpanded>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              className={classes.summary}
            >
              <Typography className={classes.heading}>Identificação</Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.details}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <label className={classes.label}>ERP *</label>
                  <Paper className={classes.medium} elevation={0}>
                    <Input
                      className={classes.input}
                      classes={{ input: classes.inputText }}
                      disabled={!isEditing}
                      value={form.erp}
                      name='erp'
                      onChange={handleInput}
                      inputProps={{ maxLength: 100 }}
                    />
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <label className={classes.label}>Versão *</label>
                  <Paper className={classes.medium} elevation={0}>
                    <Input
                      className={classes.input}
                      classes={{ input: classes.inputText }}
                      disabled={!isEditing}
                      value={form.versao}
                      name='versao'
                      onChange={handleInput}
                      inputProps={{ maxLength: 20 }}
                    />
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <label className={classes.label}>Status *</label>
                  <Paper className={classes.medium} elevation={0}>
                    <NativeSelect
                      className={classes.input}
                      disabled={!isEditing}
                      value={form.status}
                      name='status'
                      onChange={handleInput}
                    >
                      <option value='Ativo'>Ativo</option>
                      <option value='Inativo'>Inativo</option>
                    </NativeSelect>
                  </Paper>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          <Accordion defaultExpanded style={{ marginTop: 16 }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              className={classes.summary}
            >
              <Typography className={classes.heading}>Conexão</Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.details}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={8}>
                  <label className={classes.label}>URL Base *</label>
                  <Paper className={classes.medium} elevation={0}>
                    <Input
                      className={classes.input}
                      disabled={!isEditing}
                      value={form.urlBase}
                      name='urlBase'
                      onChange={handleInput}
                      placeholder='https://erp.exemplo.com.br'
                      inputProps={{ maxLength: 300 }}
                    />
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <label className={classes.label}>Tipo de Autenticação *</label>
                  <Paper className={classes.medium} elevation={0}>
                    <NativeSelect
                      className={classes.input}
                      disabled={!isEditing}
                      value={form.tipoAutenticacao}
                      name='tipoAutenticacao'
                      onChange={handleInput}
                    >
                      {TIPOS_AUTENTICACAO.map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </NativeSelect>
                  </Paper>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          <Accordion defaultExpanded style={{ marginTop: 16 }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              className={classes.summary}
            >
              <Typography className={classes.heading}>Endpoints</Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.details}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <label className={classes.label}>Endpoint Token *</label>
                  <Paper className={classes.medium} elevation={0}>
                    <Input
                      className={classes.input}
                      disabled={!isEditing}
                      value={form.endpointToken}
                      name='endpointToken'
                      onChange={handleInput}
                      placeholder='/oauth/token'
                      inputProps={{ maxLength: 300 }}
                    />
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <label className={classes.label}>Consulta Títulos *</label>
                  <Paper className={classes.medium} elevation={0}>
                    <Input
                      className={classes.input}
                      disabled={!isEditing}
                      value={form.endpointConsultaTitulos}
                      name='endpointConsultaTitulos'
                      onChange={handleInput}
                      placeholder='/api/financeiro/duplicatas'
                      inputProps={{ maxLength: 300 }}
                    />
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <label className={classes.label}>Consulta Fornecedores</label>
                  <Paper className={classes.medium} elevation={0}>
                    <Input
                      className={classes.input}
                      disabled={!isEditing}
                      value={form.endpointConsultaFornecedores}
                      name='endpointConsultaFornecedores'
                      onChange={handleInput}
                      placeholder='/api/fornecedores'
                      inputProps={{ maxLength: 300 }}
                    />
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <label className={classes.label}>Consulta Lojas</label>
                  <Paper className={classes.medium} elevation={0}>
                    <Input
                      className={classes.input}
                      disabled={!isEditing}
                      value={form.endpointConsultaLojas}
                      name='endpointConsultaLojas'
                      onChange={handleInput}
                      placeholder='/api/lojas'
                      inputProps={{ maxLength: 300 }}
                    />
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <label className={classes.label}>Duplicata Negociação Abrir</label>
                  <Paper className={classes.medium} elevation={0}>
                    <Input
                      className={classes.input}
                      disabled={!isEditing}
                      value={form.endpointDuplicataNegociacaoAbrir}
                      name='endpointDuplicataNegociacaoAbrir'
                      onChange={handleInput}
                      placeholder='/api/financeiro/duplicata-pagamento/titulos/negociacao/abrir'
                      inputProps={{ maxLength: 300 }}
                    />
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <label className={classes.label}>Duplicata Negociação Liberar</label>
                  <Paper className={classes.medium} elevation={0}>
                    <Input
                      className={classes.input}
                      disabled={!isEditing}
                      value={form.endpointDuplicataNegociacaoLiberar}
                      name='endpointDuplicataNegociacaoLiberar'
                      onChange={handleInput}
                      placeholder='/api/financeiro/duplicata-pagamento/titulos/negociacao/liberar'
                      inputProps={{ maxLength: 300 }}
                    />
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <label className={classes.label}>Duplicata Antecipar Título</label>
                  <Paper className={classes.medium} elevation={0}>
                    <Input
                      className={classes.input}
                      disabled={!isEditing}
                      value={form.endpointDuplicataAnteciparTitulo}
                      name='endpointDuplicataAnteciparTitulo'
                      onChange={handleInput}
                      placeholder='/api/financeiro/duplicata-pagamento/titulos/antecipar'
                      inputProps={{ maxLength: 300 }}
                    />
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <label className={classes.label}>Duplicata Trocar Cedente</label>
                  <Paper className={classes.medium} elevation={0}>
                    <Input
                      className={classes.input}
                      disabled={!isEditing}
                      value={form.endpointDuplicataTrocarCedente}
                      name='endpointDuplicataTrocarCedente'
                      onChange={handleInput}
                      placeholder='/api/financeiro/duplicatas'
                      inputProps={{ maxLength: 300 }}
                    />
                  </Paper>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {!isRegister && (
            <Accordion defaultExpanded style={{ marginTop: 16 }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                className={classes.summary}
              >
                <Typography className={classes.heading}>Registro</Typography>
              </AccordionSummary>
              <AccordionDetails className={classes.details}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <label className={classes.label}>Data de Registro</label>
                    <Paper className={classes.medium} elevation={0}>
                      <Input
                        className={classes.input}
                        disabled
                        value={
                          form.dataRegistro
                            ? formatDateTimeToRender(form.dataRegistro)
                            : ''
                        }
                      />
                    </Paper>
                  </Grid>
                  <Grid item xs={6}>
                    <label className={classes.label}>Data de Atualização</label>
                    <Paper className={classes.medium} elevation={0}>
                      <Input
                        className={classes.input}
                        disabled
                        value={
                          form.dataAtualizacao
                            ? formatDateTimeToRender(form.dataAtualizacao)
                            : ''
                        }
                      />
                    </Paper>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          )}
        </div>
      </div>
    </div>
  );
}
